import { OnboardingFlow } from '@/types/onboarding.types';
import {
  useOnboardingStore,
  OnboardingStep,
} from '@/V2/stores/pinia/onboarding.store';
import { RiskAnalysisOnboardingFlow } from '@/shared/services/onboarding/instances/risk-analysis.onboarding';
import { useRoute } from 'vue-router';
import { readStorage, writeStorage } from '@/utils/storage';
import OnboardingLaunchScreen from '@/shared/components/onboarding/OnboardingLaunchScreen.vue';
import OnboardingTooltip from '@/shared/components/onboarding/OnboardingTooltip.vue';
import { createVNode, render } from 'vue';
import { InscribeEventsLogger, INSCRIBE_EVENTS } from '@/events-logger';

const ONBOARDING_FLOW_STORAGE_KEY = 'onboarding-flow';
const ONBOARDING_INTERCOM_ARTICLES_STORAGE_KEY = 'onboarding-intercom-articles';

export enum OnboardingFlowEventType {
  STARTED = 'started',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  LAUNCH_SCREEN_OPENED = 'launch-screen-opened',
  STEP_SHOWN = 'step-shown',
  RESET = 'reset',
  TARGET_NOT_FOUND = 'target-not-found',
  INTERCOM_ARTICLES_DISMISSED = 'intercom-articles-dismissed',
  INTERCOM_ARTICLES_OPENED = 'intercom-articles-opened',
}

export const OnboardingService = {
  onboardingEnabled: !navigator.webdriver,

  /**
   * this is the entry point for the onboarding service
   * it will register the onboarding types and start the onboarding process
   */
  init: () => {
    if (!OnboardingService.onboardingEnabled) {
      return;
    }
    // register onboarding types
    OnboardingService.registerOnboardingFlow(RiskAnalysisOnboardingFlow);
  },

  /**
   * this function is called to check if the onboarding should be activated for the current route
   * it is called on every route change
   * @param route
   * @returns
   */
  onRouteChange: () => {
    const route = useRoute();
    if (route.name === 'login') {
      // if you hit a route that has an onboarding flow with no session, close the launch screen
      OnboardingService.closeLaunchScreen();
      return;
    }
    const store = useOnboardingStore();
    if (
      !OnboardingService.onboardingEnabled ||
      !store.hasEnabledOnboardingFlows
    ) {
      return;
    }
    for (const onboardingFlow of store.getEnabledOnboardingFlows) {
      const store = useOnboardingStore();
      if (
        store.isOnboardingFlowEnabled(onboardingFlow) &&
        onboardingFlow.activationRoutes.includes(route.name as string)
      ) {
        OnboardingService.startOnboardingFlow(onboardingFlow);
        break;
      }
    }
  },

  /**
   * this checks if the onboarding should be activated.
   * It checks the local storage to see if it has been skipped before.
   * @param route
   */
  shouldActivateOnboardingFlow: (onboardingFlow: OnboardingFlow) => {
    if (
      readStorage(OnboardingService.getOnboardingFlowStorageKey(onboardingFlow))
    ) {
      return false;
    }
    return true;
  },

  /**
   * this method is called to check if the onboarding intercom articles should be shown for the current route
   * @param onboardingFlow
   * @returns
   */
  shouldShowOnboardingIntercom: (onboardingFlow: OnboardingFlow) => {
    const storageKey =
      OnboardingService.getOnboardingIntercomArticlesStorageKey(onboardingFlow);
    return !readStorage(storageKey);
  },

  /**
   * this methods starts an onbarding flow and can be called programmatically from the UI
   * @param onboardingFlow
   */
  startOnboardingFlow: (onboardingFlow: OnboardingFlow) => {
    const store = useOnboardingStore();
    store.setActiveOnboardingFlow(onboardingFlow);
    if (onboardingFlow.launchScreenProps) {
      OnboardingService.openLaunchScreen();
    } else {
      OnboardingService.showNextStep();
    }
    OnboardingService.logOnboardingFlowEvent({
      eventType: OnboardingFlowEventType.STARTED,
    });
  },

  /**
   * this method is called to show the next step in the onboarding flow
   */
  showNextStep: () => {
    const store = useOnboardingStore();
    const steps = store.getActiveOnboardingFlow?.steps || [];
    const currentStep = store.getOnboardingFlowActiveStep;
    let nextStep;
    if (!currentStep) {
      nextStep = steps?.[0];
    } else if (currentStep.index >= steps.length) {
      OnboardingService.completeOnboardingFlow();
      return;
    } else {
      const nextStepIndex = currentStep.index + 1;
      nextStep = steps.find(
        (step: OnboardingStep) => step.index === nextStepIndex,
      );
    }
    OnboardingService.showOnboardingFlowStep(nextStep);
  },

  /**
   * this method is called to show the previous step in the onboarding flow
   */
  showPreviousStep: () => {
    const store = useOnboardingStore();
    const steps = store.getActiveOnboardingFlow?.steps;
    const currentStep = store.getOnboardingFlowActiveStep;
    let previousStep;
    if (!currentStep || currentStep.index === 1) {
      previousStep = steps?.[0];
    } else {
      const previousStepIndex = currentStep.index - 1;
      previousStep = steps?.find(
        (step: OnboardingStep) => step.index === previousStepIndex,
      );
    }
    OnboardingService.showOnboardingFlowStep(previousStep);
  },

  /**
   * this method is called to complete the onboarding flow
   * it will reset the current onboarding flow and step
   */
  completeOnboardingFlow: () => {
    const store = useOnboardingStore();
    OnboardingService.markOnboardingFlowAsCompleted(
      store.getActiveOnboardingFlow as OnboardingFlow,
    );
    OnboardingService.logOnboardingFlowEvent({
      eventType: OnboardingFlowEventType.COMPLETED,
    });
    store.setActiveOnboardingFlow(null as OnboardingFlow);
    store.resetActiveOnboardingFlow();
  },

  /**
   * shows the onboarding flow Step. If there is already a tooltip component do not create one.
   * @param onboardingFlowStep
   */
  showOnboardingFlowStep: (onboardingFlowStep: OnboardingFlowStep) => {
    let tooltipContainer: Element | ShadowRoot;
    const store = useOnboardingStore();
    store.setActiveOnboardingFlowStep(onboardingFlowStep);
    const existingTooltipComponent = document.querySelector(
      '.onboarding-tooltip-container',
    );
    if (!existingTooltipComponent) {
      tooltipContainer = document.createElement('div');
      tooltipContainer.classList.add('onboarding-tooltip-container');
      document.body.appendChild(tooltipContainer);
      const vNode = createVNode(OnboardingTooltip, {
        onClose: () => {
          render(null, tooltipContainer);
          (tooltipContainer as HTMLElement).remove();
        },
      });
      render(vNode, tooltipContainer);
      vNode.component?.exposed?.openOnboardingTooltip();
    }
    OnboardingService.logOnboardingFlowEvent({
      eventType: OnboardingFlowEventType.STEP_SHOWN,
      stepIndex: onboardingFlowStep?.index,
    });
  },

  /**
   * called to skip the onboarding flow. It can be called programmatically from the UI.
   * @param onboardingFlow
   */
  skipOnboardingFlow: (
    onboardingFlow: OnboardingFlow,
    logEvent: boolean = true,
  ) => {
    const store = useOnboardingStore();
    store.setActiveOnboardingFlow(null as OnboardingFlow);
    store.resetActiveOnboardingFlow();
    OnboardingService.markOnboardingFlowAsCompleted(onboardingFlow);
    if (logEvent) {
      OnboardingService.logOnboardingFlowEvent({
        eventType: OnboardingFlowEventType.SKIPPED,
      });
    }
  },

  /**
   * this method is called to register an onboarding flow
   * @param onboardingFlow
   */
  registerOnboardingFlow: (onboardingFlow: OnboardingFlow) => {
    const store = useOnboardingStore();
    store.addOnboardingFlow(
      onboardingFlow,
      OnboardingService.shouldActivateOnboardingFlow(onboardingFlow),
      OnboardingService.shouldShowOnboardingIntercom(onboardingFlow),
    );
  },

  /**
   * this method is called to reset the onboarding service
   */
  resetOnboardingBrowserState: () => {
    OnboardingService.resetLocalStorage();
    const store = useOnboardingStore();
    store.resetState();
    OnboardingService.init();
    OnboardingService.logOnboardingFlowEvent({
      eventType: OnboardingFlowEventType.RESET,
    });
  },

  /**
   * this method is called to reset the local storage for the onboarding flows
   */
  resetLocalStorage: () => {
    const store = useOnboardingStore();
    for (const onboardingFlow of store.getAvailableOnboardingFlows) {
      localStorage.removeItem(
        OnboardingService.getOnboardingFlowStorageKey(onboardingFlow),
      );
      localStorage.removeItem(
        OnboardingService.getOnboardingIntercomArticlesStorageKey(
          onboardingFlow,
        ),
      );
    }
  },

  /**
   * this method is called to mark an onboarding flow as completed by writing to the local storage
   *  @param onboardingFlow
   */
  markOnboardingFlowAsCompleted: (onboardingFlow: OnboardingFlow) => {
    const store = useOnboardingStore();
    store.resetActiveOnboardingFlow();
    writeStorage(
      OnboardingService.getOnboardingFlowStorageKey(onboardingFlow),
      true,
    );
  },

  /**
   * this method is called to mark the onboarding intercom articles as dismissed by writing to the local storage
   * @param onboardingFlow
   */
  markOnboardingIntercomAsDismissed: (onboardingFlow: OnboardingFlow) => {
    const store = useOnboardingStore();
    store.setIntercomArticlesDismissed(onboardingFlow);
    writeStorage(
      OnboardingService.getOnboardingIntercomArticlesStorageKey(onboardingFlow),
      true,
    );
  },

  /**
   * This opens the launch screen for the onboarding flow.
   * It dynamically loads an instance of a OnboardingLaunchScreen component
   * @param onboardingFlow
   */
  openLaunchScreen: () => {
    const store = useOnboardingStore();
    store.setActiveOnboardingFlowStep(null);
    const launchScreenContainer = document.createElement('div');
    launchScreenContainer.classList.add('onboarding-launch-screen-container');
    document.body.appendChild(launchScreenContainer);
    const vNode = createVNode(OnboardingLaunchScreen, {
      onClose: () => {
        render(null, launchScreenContainer);
        (launchScreenContainer as HTMLElement).remove();
      },
    });
    render(vNode, launchScreenContainer);
    vNode.component?.exposed?.openOnboardingLaunchScreen();
    OnboardingService.logOnboardingFlowEvent({
      eventType: OnboardingFlowEventType.LAUNCH_SCREEN_OPENED,
    });
  },

  /**
   * this method is called to close the launch screen for the onboarding flow
   */
  closeLaunchScreen: () => {
    const launchScreenContainer = document.querySelector(
      '.onboarding-launch-screen-container',
    );
    if (launchScreenContainer) {
      render(null, launchScreenContainer);
      (launchScreenContainer as HTMLElement).remove();
    }
  },

  /**
   * this method returns the storage key for an onboarding flow. It will be used to store the onboarding flow in the local storage.
   * @param onboardingFlow
   * @returns
   */
  getOnboardingFlowStorageKey: (onboardingFlow: OnboardingFlow) => {
    return `${ONBOARDING_FLOW_STORAGE_KEY}-${onboardingFlow.key}`;
  },

  /**
   * this method returns the storage key for the onboarding intercom articles. It will be used to store the onboarding intercom articles in the local storage.
   * @param onboardingFlow
   * @returns
   */
  getOnboardingIntercomArticlesStorageKey: (onboardingFlow: OnboardingFlow) => {
    return `${ONBOARDING_INTERCOM_ARTICLES_STORAGE_KEY}-${onboardingFlow.key}`;
  },

  /**
   * this method is called to log an event for an onboarding flow to Datadog
   * @param onboardingFlow
   * @param eventType
   */
  logOnboardingFlowEvent(data: object) {
    const store = useOnboardingStore();
    InscribeEventsLogger.logEvent(INSCRIBE_EVENTS.ONBOARDING_FLOW, {
      onboardingFlowKey: store.getActiveOnboardingFlow?.key,
      ...data,
    });
  },
};
